import { useState } from "react";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { Search, BadgeCheck, XCircle, ShieldCheck } from "lucide-react";

export default function SiretVerifier({ initial = "", onVerified, compact = false, captureOnly = false }) {
  const [siret, setSiret] = useState(initial);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const verify = async () => {
    const raw = siret.replace(/\s/g, "");
    if (!/^\d{9}(\d{5})?$/.test(raw)) {
      toast.error("SIRET (14 chiffres) ou SIREN (9 chiffres) attendu");
      return;
    }
    setBusy(true);
    setData(null);
    try {
      const { data: d } = await apiClient.get(`/siret/verify?siret=${raw}`);
      setData(d);
      if (d.valid) {
        toast.success(`Vérifié : ${d.denomination}`);
        if (captureOnly) onVerified?.({ ...d, siret: raw });
      } else {
        toast.error(d.message || "Entreprise non trouvée");
      }
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveOnProfile = async () => {
    if (!data?.valid) return;
    setSaving(true);
    try {
      await apiClient.post("/users/me/verify-siret", {
        siret: siret.replace(/\s/g, ""),
        siret_data: data,
      });
      toast.success("SIRET vérifié et enregistré sur votre profil !");
      onVerified?.(data);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`border-2 border-[#0A0A0B] bg-white ${compact ? "p-4" : "p-6"}`}>
      <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-[#FF4D00]">
        <BadgeCheck className="w-3.5 h-3.5" /> // Vérification SIRET
      </div>
      <div className="text-xs text-zinc-600 mt-1">
        Source officielle <strong>recherche-entreprises.api.gouv.fr</strong> · gratuit · INSEE/Sirene
      </div>

      <div className="flex gap-2 mt-3">
        <input
          value={siret}
          onChange={(e) => { setSiret(e.target.value); setData(null); }}
          placeholder="14 chiffres (SIRET) ou 9 chiffres (SIREN)"
          className="flex-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white mono text-sm"
          data-testid="siret-verifier-input"
        />
        <button
          type="button"
          onClick={verify}
          disabled={busy || !siret.trim()}
          className="btn-anthracite text-xs flex items-center gap-2 disabled:opacity-60"
          data-testid="siret-verifier-button"
        >
          {busy ? "..." : <><Search className="w-3.5 h-3.5" /> Vérifier</>}
        </button>
      </div>

      {data?.valid && (
        <>
          <div className="mt-3 border-2 border-emerald-600 bg-emerald-50 p-3" data-testid="siret-result-ok">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
              <ShieldCheck className="w-4 h-4" /> {data.denomination}
              {data.active ? (
                <span className="badge-pill bg-emerald-600 text-white border-emerald-700">Actif</span>
              ) : (
                <span className="badge-pill bg-red-600 text-white border-red-700">Cessé</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-emerald-900">
              {data.naf_code && <div>NAF : <strong>{data.naf_code}</strong></div>}
              {data.city && <div>Siège : <strong>{data.city}</strong></div>}
              {data.creation_date && <div>Création : <strong>{data.creation_date}</strong></div>}
              {data.legal_form && <div>Forme : <strong>{data.legal_form}</strong></div>}
            </div>
          </div>
          {captureOnly ? (
            <div className="mt-3 mono text-[10px] uppercase tracking-widest text-emerald-700 text-center" data-testid="siret-captured">
              ✓ SIRET prêt à être enregistré à la création du compte
            </div>
          ) : (
            <button
              type="button"
              onClick={saveOnProfile}
              disabled={saving}
              className="btn-batisur w-full mt-3 flex items-center justify-center gap-2"
              data-testid="siret-save-profile"
            >
              <BadgeCheck className="w-4 h-4" />
              {saving ? "Enregistrement…" : "Enregistrer sur mon profil"}
            </button>
          )}
        </>
      )}
      {data && !data.valid && (
        <div className="mt-3 border-2 border-red-300 bg-red-50 p-3 flex items-center gap-2 text-sm text-red-800">
          <XCircle className="w-4 h-4" /> {data.message}
        </div>
      )}
    </div>
  );
}
